import { useState } from "react"
import { Link } from "react-router-dom"
import { ArrowLeft, CheckCircle2 } from "lucide-react"
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldError,
  FieldSeparator,
} from '@/shared/components/ui/field'
import { Input } from '@/shared/components/ui/input'
import { authApi } from '@/shared/services/auth'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'

export default function ForgotPasswordPage() {
  useDocumentTitle("Recuperar contraseña")
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const response = await authApi.requestPasswordReset({ email })
      setSuccessMessage(response.message)
    } catch {
      setError("Error de conexión. Por favor intenta de nuevo.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10 bg-[#121212]">
      <div className="relative z-10 flex w-full max-w-md flex-col gap-6">
        <Card className="shadow-lg border-0">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Recuperar contraseña</CardTitle>
            <CardDescription className="text-base">
              Ingresa tu correo y te enviaremos un enlace para restablecerla
            </CardDescription>
          </CardHeader>
          <CardContent>
            {successMessage ? (
              <FieldGroup className="gap-4">
                <div className="flex flex-col items-center gap-3 py-4 text-center">
                  <CheckCircle2 className="h-12 w-12 text-green-600" />
                  <p className="text-sm text-muted-foreground">{successMessage}</p>
                </div>
                <Button asChild variant="outline" className="w-full h-12 text-base">
                  <Link to="/login">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Volver al inicio de sesión
                  </Link>
                </Button>
              </FieldGroup>
            ) : (
              <form onSubmit={handleSubmit}>
                <FieldGroup className="gap-4">
                  <Field>
                    <div className="flex items-center justify-center py-1">
                      <img src="/logo-tonal-negro.png" alt="Tonal" className="h-24 w-auto" />
                    </div>
                  </Field>

                  <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
                    Recuperación
                  </FieldSeparator>

                  {error && (
                    <FieldError className="text-center bg-red-50 border border-red-200 p-3 rounded-md">
                      {error}
                    </FieldError>
                  )}

                  <Field className="gap-1">
                    <FieldLabel htmlFor="email" className="text-sm">Correo electrónico</FieldLabel>
                    <Input
                      id="email"
                      type="email"
                      placeholder="correo@ejemplo.com"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={isLoading}
                      autoComplete="username"
                      className="h-12 text-base"
                    />
                  </Field>

                  <Field>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="w-full h-12 text-base text-white shadow-md bg-[#1e1e1e] hover:bg-[#2a2a2a] border-[#1e1e1e]"
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Enviando...
                        </>
                      ) : (
                        "Enviar enlace"
                      )}
                    </Button>
                  </Field>

                  <Field>
                    <Link
                      to="/login"
                      className="text-sm text-center text-muted-foreground hover:text-foreground"
                    >
                      Volver al inicio de sesión
                    </Link>
                  </Field>
                </FieldGroup>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
